import { useQuery } from '@tanstack/react-query';
import serviceAccounts from 'services/accounts';

/**
 * @typedef {Object} EmailMonthlyUsage
 * @property {number} id - Unique identifier for the usage record
 * @property {string} start_date - Start date of the billing period (ISO date string)
 * @property {number} monthly_emails_count - Base email allocation for the month
 * @property {number} total_bulk_sent - Total bulk emails sent during the period
 * @property {number} extra - Number of emails exceeding the base allocation
 * @property {number} extra_cost - Cost incurred for extra emails
 */

/**
 * @typedef {Object} SmsMonthlyUsage
 * @property {number} id - Unique identifier for the usage record
 * @property {string} start_date - Start date of the billing period (ISO date string)
 * @property {number} total_sent_segments - Total SMS segments sent
 * @property {number} total_received_segments - Total SMS segments received
 * @property {number} total_segments - Combined total of sent and received segments
 * @property {number} segments_cost - Cost incurred for SMS segments
 */

/**
 * @typedef {Object} UsageStat
 * @property {number|null} value - The stat value, null when integration is inactive
 * @property {string|null} updated_at - ISO timestamp of last calculation, null if always live
 */

/**
 * @typedef {Object} AccountUsageData
 * @property {EmailMonthlyUsage[]} email_usage - Email usage broken down by month
 * @property {SmsMonthlyUsage[]} sms_usage - SMS usage broken down by month
 * @property {UsageStat} tremendous_balance - Current Tremendous incentive balance
 * @property {UsageStat} first_party_balance - Current first-party incentive balance
 * @property {UsageStat} total_users - Total number of users on the account
 * @property {UsageStat} paid_users - Number of paid users (admins and researchers, excluding viewers)
 * @property {UsageStat} total_panels - Total number of panels on the account
 * @property {UsageStat} total_panelists - Total number of panelists on the account (cron-calculated)
 */

/**
 * Query hook for fetching account usage data including email and SMS usage
 * breakdowns by month, incentive balances, and account-wide totals.
 *
 * @param {import('@tanstack/react-query').UseQueryOptions<AccountUsageData>} [options] - Additional useQuery options
 * @returns {import('@tanstack/react-query').UseQueryResult<AccountUsageData>}
 */
export function useAccountUsageQuery(options = {}) {
    return useQuery({
        queryKey: ['account-usage'],
        queryFn: () => serviceAccounts.getUsage(),
        ...options
    });
}
